import {ChangeDetectorRef, Component, inject, Inject, PLATFORM_ID} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CartAddEntryFailEvent} from '@spartacus/cart/base/root';
import {EventService, GlobalMessageService, RoutingService} from '@spartacus/core';
import {OrderHistoryFacade} from '@spartacus/order/root';
import {
  CommonConfigurator,
  CommonConfiguratorUtilsService,
  ConfiguratorRouter,
  ConfiguratorRouterExtractorService,
} from '@spartacus/product-configurator/common';
import {
  Configurator,
  ConfiguratorAddToCartButtonComponent,
  ConfiguratorCartService,
  ConfiguratorCommonsService,
  ConfiguratorGroupsService,
  ConfiguratorQuantityService,
  ConfiguratorStorefrontUtilsService,
} from '@spartacus/product-configurator/rulebased';
import {IntersectionService} from '@spartacus/storefront';
import {Observable} from 'rxjs';
import {first, map, skip} from 'rxjs/operators';
import {
  CpsConfiguratorFormValidationService,
  LocalStorageService,
  PendingConfiguratorRequestService,
} from '../../services';
import {ButtonActionCodes} from '../../models';
import {optionalServiceProviderTokens, serviceProviderTokens, valueProviderTokens} from '../../token/tokens';

const REVIEW_ORDER_ROUTE = '/checkout/review-order';

@UntilDestroy()
@Component({
  selector: 'dcp-configurator-add-to-cart-button',
  templateUrl: './dcp-configurator-add-to-cart-button.component.html',
  styleUrls: ['./dcp-configurator-add-to-cart-button.scss'],
})
export class DcpConfiguratorAddToCartButtonComponent extends ConfiguratorAddToCartButtonComponent {
  private cartDecoratorErrorHandlingService = inject(
    serviceProviderTokens.CART_DECORATOR_ERROR_HANDLING_SERVICE_PROVIDER,
  );
  private dcpActiveCartService = inject(serviceProviderTokens.ACTIVE_CART_SERVICE_PROVIDER);
  private addToCartEventService = inject(optionalServiceProviderTokens.ADD_TO_CART_EVENT_SERVICE_PROVIDER, {
    optional: true,
  });

  showSpinner: boolean = false;
  hasExternalError: boolean = false;
  hasError$: Observable<boolean> = this.cartDecoratorErrorHandlingService.getErrorState$().pipe(map((e) => e.hasError));

  constructor(
    public override routingService: RoutingService,
    public override configuratorCommonsService: ConfiguratorCommonsService,
    public override configuratorCartService: ConfiguratorCartService,
    public override configuratorGroupsService: ConfiguratorGroupsService,
    public override configRouterExtractorService: ConfiguratorRouterExtractorService,
    public override globalMessageService: GlobalMessageService,
    public configuratorFormValidationService: CpsConfiguratorFormValidationService,
    protected localStorageService: LocalStorageService,
    private eventService: EventService,
    private cdf: ChangeDetectorRef,
    @Inject(PLATFORM_ID) public platformId: object,
    configUtils: ConfiguratorStorefrontUtilsService,
    orderHistoryFacade: OrderHistoryFacade,
    commonConfiguratorUtilsService: CommonConfiguratorUtilsService,
    intersectionService: IntersectionService,
    public pendingConfiguratorRequestService: PendingConfiguratorRequestService,
    configuratorQuantityService: ConfiguratorQuantityService,
  ) {
    super(
      routingService,
      configuratorCommonsService,
      configuratorCartService,
      configuratorGroupsService,
      configRouterExtractorService,
      globalMessageService,
      orderHistoryFacade,
      commonConfiguratorUtilsService,
      configUtils,
      intersectionService,
      configuratorQuantityService,
    );
  }

  override onAddToCart(configuration: Configurator.Configuration, routerData: ConfiguratorRouter.Data): void {
    this.showSpinner = true;
    this.eventService
      .get(CartAddEntryFailEvent)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.showSpinner = false;
        this.hasExternalError = true;
        this.cdf.detectChanges();
      });
    if (this.addToCartEventService) {
      this.dcpActiveCartService
        .getActiveCart()
        .pipe(untilDestroyed(this), skip(1), first())
        .subscribe((cart) => {
          this.addToCartEventService?.triggerAddToCartEvent(cart, configuration);
        });
      this.addToCartEventService.triggerEddlAddToCartEvent(configuration);
    }
    if (configuration.isCartEntryUpdateRequired) {
      this.pendingConfiguratorRequestService.setUpdateFlag(true);
    }
    super.onAddToCart(configuration, routerData);
  }

  override performNavigation(
    configuratorType: string,
    owner: CommonConfigurator.Owner,
    isAdd: boolean,
    isOverview: boolean,
    showMessage: boolean,
  ): void {
    const messageKey = isAdd ? 'configurator.addToCart.confirmation' : 'configurator.addToCart.confirmationUpdate';

    if (this.localStorageService.getItem('lastPage')?.includes(REVIEW_ORDER_ROUTE)) {
      this.routingService.go({cxRoute: 'checkoutReviewOrder'});
    } else {
      this.navigateToCart();
    }

    if (showMessage) {
      this.displayConfirmationMessage(messageKey);
    }
  }

  /**
   * This function is returning null if the customer is not in edit mode
   *
   * @param routerData
   * @param configuration
   */
  override getButtonResourceKey(
    routerData: ConfiguratorRouter.Data,
    configuration: Configurator.Configuration,
  ): string {
    if (routerData.isOwnerCartEntry && configuration.isCartEntryUpdateRequired) {
      return 'configurator.addToCart.buttonUpdateCart';
    } else if (routerData.isOwnerCartEntry && !configuration.isCartEntryUpdateRequired) {
      return 'configurator.addToCart.buttonContinueToCart';
    } else {
      return '';
    }
  }

  isPrimaryBtn(button: Configurator.ButtonAction) {
    return (
      button.code === ButtonActionCodes.ADD_TO_CART ||
      button.code === ButtonActionCodes.EXTERNAL_CHECKOUT ||
      button.code === ButtonActionCodes.LEAD_BUY
    );
  }

  redirectToTargetUrl(targetUrl: string) {
    window.location.href = targetUrl;
  }
}
